import { Activity, DollarSign, TrendingUp } from 'lucide-react';
import { DealPerformance } from './DealPerformance';
import { PipelineStatus } from './PipelineStatus';
import { FinanceTable } from './FinanceTable';
import { SalesData, PipelineStage, SalesPerformanceDataPoint } from './types';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Skeleton } from 'antd';
import { useAuth } from '../../providers/AuthProvider';

interface ResultsSectionProps {
  salesData: SalesData;
  salesPerformanceData: SalesPerformanceDataPoint[];
  salesOrderAmount: number;
  revenue: number;
  finacePermance: any[];
  isLoading: boolean;
  error: string | null;
  pipelineData: PipelineStage[];
  periodLabel: string;
  groupBy: 'day' | 'week' | 'month';
}

export const ResultsSection = ({
  salesData,
  salesPerformanceData,
  salesOrderAmount,
  revenue,
  finacePermance,
  isLoading,
  error,
  pipelineData,
  periodLabel,
  groupBy,
}: ResultsSectionProps) => {
  const { userInfo } = useAuth();
  const currencySymbol = userInfo?.company_base_currency_symbol || '$';
  const currency = (n: number) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (isLoading) {
    return (
      <Card className="rounded shadow p-4">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between mb-4">
            <CardTitle className="flex items-center gap-2 text-xl font-semibold">
              <Skeleton.Avatar active size="small" />
              <Skeleton.Input active size="small" style={{ width: 120, height:25 }} />
            </CardTitle>
            </div>
          </CardHeader>
          <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="bg-slate-50 p-6 rounded-xl animate-pulse">
                <div className="h-8 w-8 bg-slate-200 rounded-lg mb-4" />
                <div className="h-8 w-32 bg-slate-200 rounded mb-4" />
                <div className="h-4 w-24 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded shadow p-4">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between mb-4">
        <CardTitle className="flex items-center gap-2 text-xl font-semibold">
          <Activity className="h-5 w-5 text-slate-900" />
            Results
            <span className="ml-2 text-xs font-medium px-2 py-1 bg-violet-600 text-white rounded-full">
              {periodLabel}
            </span>
          </CardTitle>
          </div>
          </CardHeader>
      <CardContent className="space-y-6">
        {/* Metric Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 pt-4">
              <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Activity className="h-4 w-4 text-blue-600" />
              </div>
              <span className="text-sm font-medium text-blue-600">
                Total Sales Order received
              </span>
            </div>
            <div className="px-4 py-6">
              <span className="text-3xl font-semibold text-slate-900">
                {salesData.total_orders}
              </span>
            </div>
          </div>

          <div className="bg-emerald-50 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 pt-4">
              <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <DollarSign className="h-4 w-4 text-emerald-600" />
              </div>
              <span className="text-sm font-medium text-emerald-600">
                Open SO Amount
              </span>
            </div>
            <div className="px-4 py-6">
              <span className="text-3xl font-semibold text-slate-900">
                {currencySymbol}{currency(salesOrderAmount)}
              </span>
            </div>
          </div>

          <div className="bg-violet-50 rounded-xl overflow-hidden">
            <div className="flex items-center gap-2 px-4 pt-4">
              <div className="w-8 h-8 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-4 w-4 text-violet-600" />
              </div>
              <span className="text-sm font-medium text-violet-600">
                Total Revenue generated
              </span>
            </div>
            <div className="px-4 py-6">
              <span className="text-3xl font-semibold text-slate-900">
                {currencySymbol}{currency(revenue)}
              </span>
            </div>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div className=" rounded-xl">
            <DealPerformance
              data={salesPerformanceData}
              groupBy={groupBy}
            />
          </div>

          <div className=" rounded-xl">
            <PipelineStatus data={pipelineData} />
          </div>
        </div>

        {/* Finance Table */}
        <div className="mt-6">
          <FinanceTable loading={isLoading} error={error} data={finacePermance} />
        </div>
      </CardContent>
    </Card>
  );
};
